/**
 * The customer account's own fields (a customer is an account plus its primary subject,
 * ADR-022): login e-mail / phone, notification language and the invoice details. Identity data
 * are the primary subject's (./subjectDraft.ts, the subject form). The checks made before
 * sending use the server's field names and codes (subsystems/commerce/facade/adapters/admin-api/src/admin/customer_profile.rs).
 *
 * Invoice buyer: "same as the primary subject" by default; otherwise its own name, address,
 * e-mails and a tax code that follows the primary subject's kind — an individual's MST is its
 * CCCD, a household business's the owner's CCCD, an enterprise's 10 or 10-3 digits
 * (./taxCode.ts).
 *
 * Pure functions (no React): `npm test` covers them.
 */

import type { CustomerAccount, CustomerProfileInput, NewCustomer, NotificationLanguage } from '../api/areas/accounts';
import { emailOk, postalCodeOk, subjectInput, type SubjectDraft } from './subjectDraft.ts';
import { normalizeTaxCode, type TaxpayerKind } from './taxCode.ts';

export { emailOk, postalCodeOk };
export { cccdOk } from './taxCode.ts';

export interface AccountDraft {
  /** The login e-mail (the OWNER sign-in user's). */
  email: string;
  phone: string;
  language: NotificationLanguage;
  invoiceSame: boolean;
  invoiceBuyerName: string;
  invoiceTaxCode: string;
  invoiceAddress: string;
  /** Comma / line separated. */
  invoiceEmails: string;
}

export interface FieldProblem {
  field: string;
  code: string;
}

export function blankAccount(): AccountDraft {
  return { email: '', phone: '', language: 'vi', invoiceSame: true, invoiceBuyerName: '', invoiceTaxCode: '', invoiceAddress: '', invoiceEmails: '' };
}

/** The draft of an existing account (a masked invoice tax code is not taken over). */
export function accountFromCustomer(a: CustomerAccount): AccountDraft {
  const country = (a.country_code ?? 'VN').toUpperCase();
  const tax = a.invoice_tax_code ?? '';
  return {
    email: a.email,
    phone: a.phone ?? '',
    language: a.notification_language ?? (country === 'VN' ? 'vi' : 'en'),
    invoiceSame: a.invoice_same_as_account ?? true,
    invoiceBuyerName: a.invoice_buyer_name ?? '',
    invoiceTaxCode: tax.includes('*') ? '' : tax,
    invoiceAddress: a.invoice_address ?? '',
    invoiceEmails: (a.invoice_emails ?? []).join(', '),
  };
}

const compact = (v: string) => v.replace(/\s+/g, '');
const opt = (v: string) => (v.trim() ? v.trim().replace(/\s+/g, ' ') : null);

export function splitEmails(v: string): string[] {
  return v
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
}

/** E.164 (`+84912345678`); Vietnamese numbers may be typed nationally. null: not a phone number. */
export function toE164(value: string, country: string): string | null {
  const t = value.trim();
  if (!t || !/^[\d\s.\-()+]+$/.test(t)) return null;
  let digits = t.replace(/\D/g, '');
  const international = t.startsWith('+') || t.startsWith('00');
  if (international) {
    if (t.startsWith('00')) digits = digits.slice(2);
  } else if (country === 'VN') {
    if (digits.startsWith('0')) digits = `84${digits.slice(1)}`;
    else if (!(digits.startsWith('84') && digits.length === 11)) return null;
  } else {
    return null;
  }
  if (digits.startsWith('840')) digits = `84${digits.slice(3)}`;
  if (digits.length < 8 || digits.length > 15 || digits.startsWith('0')) return null;
  if (digits.startsWith('84')) {
    const n = digits.length - 2;
    if (n < 9 || n > 10) return null;
  }
  return `+${digits}`;
}

/** An enterprise / organisation tax code (10, 10-3 or 13 digits; see ./taxCode.ts). */
export function vnTaxCodeOk(v: string): boolean {
  return normalizeTaxCode('ORGANIZATION', v).ok;
}

/** The taxpayer kind of a subject type (the invoice tax code's rule). */
export function taxpayerOf(subjectType: string): TaxpayerKind {
  return subjectType === 'INDIVIDUAL' ? 'INDIVIDUAL' : subjectType === 'HOUSEHOLD_BUSINESS' ? 'HOUSEHOLD_BUSINESS' : 'ORGANIZATION';
}

/**
 * The checks the server makes on the account's fields, with its field names and codes.
 * `subjectType`: the primary subject's type; `country`: its address country.
 */
export function checkAccount(d: AccountDraft, subjectType: string, country: string): FieldProblem[] {
  const out: FieldProblem[] = [];
  const add = (field: string, code: string) => out.push({ field, code });
  const vn = country.toUpperCase() === 'VN';
  if (!emailOk(d.email)) add('email', 'EMAIL_FORMAT');
  if (d.phone.trim() && !toE164(d.phone, country.toUpperCase())) add('phone', 'PHONE_FORMAT');
  if (!d.invoiceSame) {
    if (!d.invoiceBuyerName.trim()) add('profile.invoice.buyer_name', 'REQUIRED');
    if (d.invoiceTaxCode.trim() && vn) {
      const r = normalizeTaxCode(taxpayerOf(subjectType), d.invoiceTaxCode);
      if (!r.ok) add('profile.invoice.tax_code', r.code);
    }
    const emails = splitEmails(d.invoiceEmails);
    if (!emails.length) add('profile.invoice.emails', 'REQUIRED');
    else if (emails.length > 5) add('profile.invoice.emails', 'TOO_MANY');
    else if (!emails.every(emailOk)) add('profile.invoice.emails', 'EMAIL_FORMAT');
  }
  return out;
}

/** The billing profile as sent. */
export function toProfileInput(d: AccountDraft): CustomerProfileInput {
  return {
    notification_language: d.language,
    invoice: d.invoiceSame
      ? { same_as_account: true }
      : { same_as_account: false, buyer_name: opt(d.invoiceBuyerName), tax_code: opt(compact(d.invoiceTaxCode)), address: opt(d.invoiceAddress), emails: splitEmails(d.invoiceEmails) },
  };
}

/** The phone as sent: E.164 when it reads as one. */
export function accountPhone(d: AccountDraft, country: string): string | null {
  return d.phone.trim() ? (toE164(d.phone, country.toUpperCase()) ?? d.phone.trim()) : null;
}

/** "Giống chủ thể chính": what the invoice carries, for the read-only preview — the subject's
 * name, its tax code by kind (a person's CCCD shown masked), its address and the login e-mail. */
export function invoicePreview(
  subject: { name: string; subjectType: string; taxCode: string | null; address: string | null },
  email: string,
): { buyer: string; taxCode: string | null; address: string | null; emails: string[] } {
  const person = taxpayerOf(subject.subjectType) !== 'ORGANIZATION';
  const tax = subject.taxCode ? compact(subject.taxCode) : null;
  return {
    buyer: subject.name,
    taxCode: tax ? (person ? maskPersonal(tax) : tax) : null,
    address: subject.address,
    emails: email.trim() ? [email.trim().toLowerCase()] : [],
  };
}

/** A personal number (CCCD) as shown: its last three digits. */
export function maskPersonal(v: string): string {
  const c = compact(v);
  return c.length <= 4 ? '****' : `****${c.slice(-3)}`;
}

/** The request of "Thêm khách hàng" from the two drafts: the subject's contact e-mail / phone and
 * the login e-mail / phone fill each other when one is left empty. */
export function newCustomerBody(
  a: AccountDraft,
  s: SubjectDraft,
  extra: { tier: string; currency: string; createLogin: boolean },
): { account: AccountDraft; subject: SubjectDraft; body: NewCustomer } {
  const account = { ...a, email: a.email.trim() || s.email.trim(), phone: a.phone.trim() || s.phone.trim() };
  const subject = { ...s, email: s.email.trim() || account.email, phone: s.phone.trim() || account.phone };
  // The subject form's body; the customer being created manages it.
  const { managing_customer_id: _customer, ...subjectBody } = subjectInput(subject);
  return {
    account,
    subject,
    body: {
      email: account.email.trim().toLowerCase(),
      phone: accountPhone(account, subject.country),
      billing_currency: extra.currency,
      tier: extra.tier,
      profile: toProfileInput(account),
      subject: subjectBody,
      create_login: extra.createLogin,
    },
  };
}
